import {
	getBusinessUnitFromID,
	getPostsInPageString,
} from '@tracsis/library/lib/helpers';
import {
	Button,
	ButtonGroup,
	EmptyList,
	Lead,
	Search,
	Section,
} from '@tracsis/library/src';
import {
	ArrowRight,
	LayoutCard,
	LayoutList,
} from '@tracsis/library/src/components/Icon';
import { ThemeContext } from '@tracsis/library/src/context/ThemeContext';
import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';

import ResourceCard from '../ResourceCard/ResourceCard';
import ResourceGrid from '../ResourceGrid/ResourceGrid';
import * as styles from './ResourceList.module.css';

const ResourceList = ({
	layoutDefault,
	posts,
	pageContext,
	hasSearch = false,
	hasFilters = false,
	showImages = false,
	showDates = false,
	siteName = '',
	title,
	allResources,
	searchQuery,
	filterQuery,
	background = 'grey',
	snippet,
	snippetLink,
	breaker,
}: {
	layoutDefault: 'list' | 'grid';
	posts: { edges: []; totalCount: number; length: number };
	pageContext: {
		limit: number;
		skip: number;
		businessUnit: string;
		numberOfPages: number;
		totalPosts: number;
		currentPage: number;
		search: {};
	};
	location: {};
	hasSearch?: boolean;
	showImages?: boolean;
	showDates?: boolean;
	searchQuery?: string | undefined;
	filterQuery?: string;
	background?: 'white' | 'grey';
	hasFilters?: boolean;
	siteName?: string;
	title?: string;
	allResources?: string;
	snippet?: boolean;
	snippetLink?: any;
	breaker?: React.ReactNode;
}) => {
	const { variant } = useContext(ThemeContext);
	const [layout, setLayout] = useState<'list' | 'grid'>(layoutDefault);

	useEffect(() => {
		localStorage
			? setLayout(localStorage.getItem('resourceLayout') || 'list')
			: setLayout(layoutDefault);
	}, []);

	function toggleLayout(type: 'list' | 'grid') {
		setLayout(type);
		localStorage.setItem('resourceLayout', type);
	}

	return (
		<>
			{hasSearch && (
				<Section background="darkerGrey">
					<Search
						siteName={siteName}
						action="/resources"
						label="resources"
						searchQuery={searchQuery}
						hasFilter={hasFilters}
						filterQuery={filterQuery}
					/>
					<div className={styles.layoutToggleGrid}>
						<p className={styles.layoutToggle}>
							<strong>View</strong>
							<span
								className={cx(styles[`${layout}Layout`], styles.buttonWrap)}
							>
								<span
									className={cx({ [styles.active]: layout === 'card' })}
									onClick={() => toggleLayout('card')}
								>
									<LayoutCard />
								</span>
								<span
									className={cx({ [styles.active]: layout === 'list' })}
									onClick={() => toggleLayout('list')}
								>
									<LayoutList />
								</span>
							</span>
						</p>

						{posts.length > 0 &&
							(searchQuery || filterQuery ? (
								<p>
									{posts.length} results found for '
									<strong>{searchQuery}</strong>'
								</p>
							) : (
								<p>
									Displaying{' '}
									{getPostsInPageString(
										pageContext.limit,
										pageContext.skip,
										pageContext.totalPosts
									)}{' '}
									results
								</p>
							))}
					</div>
				</Section>
			)}

			<Section padding="xl" background={background}>
				{title && (
					<Lead align="left">
						<h2>{title}</h2>

						{snippet && (
							<ButtonGroup align="right" className="section-header-actions">
								<Button to={snippetLink} theme="inline">
									View all resources
									<ArrowRight />
								</Button>
							</ButtonGroup>
						)}
					</Lead>
				)}

				{posts && posts.length > 0 ? (
					<ResourceGrid layout={layout}>
						{posts.map(({ node: post }, i: number) => {
							const halfway = Math.round(posts.length / 2);
							const story = JSON.parse(post.content);
							const { title, last_updated, image, type, business_unit } = story;
							const buLabel = getBusinessUnitFromID(business_unit);

							if (i != halfway) {
								return (
									<ResourceCard
										layout={layout}
										title={title}
										last_updated={last_updated}
										image={image}
										business_unit={buLabel}
										type={type}
										key={post.uuid}
										to={`/${post.full_slug}`}
									/>
								);
							} else {
								return (
									<>
										{breaker}
										<ResourceCard
											layout={layout}
											title={title}
											last_updated={last_updated}
											image={image}
											business_unit={buLabel}
											type={type}
											key={post.uuid}
											to={`/${post.full_slug}`}
										/>
									</>
								);
							}
						})}
					</ResourceGrid>
				) : (
					<EmptyList>
						<p>There were no resources found matching your search.</p>
					</EmptyList>
				)}

				{snippet && (
					<ButtonGroup align="center" className="section-footer-actions">
						<Button to={snippetLink} theme="inverted">
							View all resources
							<ArrowRight />
						</Button>
					</ButtonGroup>
				)}
			</Section>
		</>
	);
};

export default ResourceList;
